import { Trans } from 'react-i18next';
import { useCallback } from 'react';

import EnterFilePasswordForm from '../../../components/Forms/EnterFilePasswordForm';
import type File from '../../../interfaces/File';
import { useAppStore } from '../../../store/store';
import type { UseFilesActionsDeps } from '../deps';
import { fileService } from '../services';
import type { UseFilesActionsEvents, UseFilesActionsReturn } from '../types';

/**
 * Custom hook to handle file trash bin actions:
 * - Soft delete (move files to trash)
 * - Restore files from trash
 * - Permanently delete files from trash
 * - Empty entire trash bin
 * - Open (restore) files directly from trash UI
 */
export function useFilesTrashActions(
  deps: UseFilesActionsDeps,
): Pick<
  UseFilesActionsReturn,
  | 'handleDeleteFiles'
  | 'handleRestoreFilesFromTrash'
  | 'handlePermanentlyDeleteFiles'
  | 'handleEmptyTrash'
  | 'handleOpenFileFromTrash'
> {
  const { modal, notification, message, t } = deps;

  /**
   * Handle soft-deleting files (move to trash).
   * Shows loading, soft-deletes, and displays success or error notification.
   */
  const deleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      message.open({
        type: 'loading',
        content: t('notifications.operationMayTakeTime'),
        duration: 0,
      });

      fileService
        .updateDeletedFiles(
          files.map((f) => f.cufi),
          true,
        )
        .then(() => {
          // Trigger callback/event after files have been "soft-deleted"
          events?.onFilesDeleted?.(files);
          // Notify user of successful deletion
          message.success({
            content: t('notifications.fileDeleted', {
              resource: files[0]?.fileName || '',
              count: files?.length || 0,
            }),
          });
        })
        .catch(() => {
          // Notify user if delete failed
          notification.error({
            title: t('errors.errorDeleteFiles'),
          });
        })
        .finally(() => {
          // Remove loading message
          message.destroy();
        });
    },
    [t, notification, message],
  );

  /**
   * Permanently empty the trash bin.
   * Calls API to delete all trashed files, shows notifications and refreshes state.
   */
  const emptyTrash = useCallback(
    async (events?: UseFilesActionsEvents) => {
      message.open({
        type: 'loading',
        content: t('notifications.operationMayTakeTime'),
        duration: 0,
      });

      try {
        // Delete all files in trash (handled by empty array)
        const res = await fileService.deleteFiles([]);
        if (res.status >= 200 && res.status < 300) {
          events?.onFilesDeleted?.([]);
          message.success({ content: t('notifications.emptyTrash') });
          // Refresh UI to reflect emptied trash
          useAppStore.getState().events.emit.refreshBatches();
        } else {
          // Any non-successful response throws
          throw res;
        }
      } catch (_e: unknown) {
        notification.error({
          title: t('errors.genericDeleteError', { resource: t('common.files') }),
        });
      } finally {
        message.destroy();
      }
    },
    [t, notification, message],
  );

  /**
   * Restore files from trash back to regular listing.
   * Shows success or error notification.
   */
  const restoreFilesFromTrash = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      fileService
        .updateDeletedFiles(
          files.map((f) => f.cufi),
          false,
        )
        .then(() => {
          // Notify callbacks/UI
          events?.onFilesDeleted?.(files);
          message.success({
            content: t('notifications.fileRestored', {
              resource: files[0]?.fileName || '',
              count: files?.length || 0,
            }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.errorRestoreFiles'),
          });
        })
        .finally(() => {
          // No loader to destroy here
        });
    },
    [t, notification, message],
  );

  /**
   * Permanently delete specific files from trash.
   * Shows modal loading, and on success also triggers refresh of file batches.
   */
  const permanentlyDeleteFiles = useCallback(
    async (files: File[], events?: UseFilesActionsEvents) => {
      message.open({
        type: 'loading',
        content: t('notifications.operationMayTakeTime'),
        duration: 0,
      });

      try {
        // Call API to permanently delete given files
        const res = await fileService.deleteFiles(files.map((f) => f.cufi));
        const ok = res.status >= 200 && res.status < 300;

        if (!ok) {
          throw res;
        }

        // Notify/delete callbacks
        events?.onFilesDeleted?.(files);
        message.success({
          content: t('notifications.filePermanentlyDeleted', {
            resource: files[0]?.fileName || '',
            count: files?.length || 0,
          }),
        });

        // Refresh UI
        useAppStore.getState().events.emit.refreshBatches();
      } catch (_e) {
        notification.error({
          title: t('errors.errorPermanentlyDeleteFiles'),
        });
      } finally {
        message.destroy();
      }
    },
    [t, notification, message],
  );

  /**
   * Handles moving files to trash (soft-delete), with handling for password-protected and shortcut files,
   * showing modals or form as appropriate for confirmation.
   */
  const handleDeleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      // Find files that require password to delete
      const passwordProtectedFiles = files.filter((f) => f.isPasswordSet);

      // Prevent deletion of multiple password-protected files at once
      if (passwordProtectedFiles.length > 1) {
        notification.warning({
          message: t('errors.cannotDeleteMultiplePasswordProtected'),
        });
        return;
      }

      // Handle single password-protected file deletion (requires user to enter password)
      if (passwordProtectedFiles.length === 1) {
        const fileToUnlock = passwordProtectedFiles[0];
        if (!fileToUnlock) return;
        // Show modal to enter password for file
        const passwordModalInstance = modal.confirm({
          title: t('modal.filePasswordForm.title'),
          width: 480,
          content: (
            <EnterFilePasswordForm
              fileId={fileToUnlock.id}
              isOwner={deps.authenticatedUser?.id === fileToUnlock.ownerId}
              onCloseParentModal={() => passwordModalInstance.destroy()}
              onSubmit={() => {
                // After password is confirmed, check if there are shortcuts
                passwordModalInstance.destroy();
                if (files.some((f) => f.hasShortcuts)) {
                  // If file has shortcuts, show a confirmation modal
                  const shortcutsModalInstance = modal.confirm({
                    title: t('modal.filesWithShortcutsDeleteConfirmation.title', {
                      resource: files?.[0]?.fileName,
                      count: files.length,
                    }),
                    content: (
                      <Trans>
                        {t('modal.filesWithShortcutsDeleteConfirmation.description', {
                          resource: files?.[0]?.fileName,
                          count: files.length,
                        })}
                      </Trans>
                    ),
                    onOk: () => {
                      deleteFiles(files, events);
                    },
                    onCancel: () => {
                      shortcutsModalInstance.destroy();
                    },
                  });
                } else {
                  // No shortcuts: delete directly
                  deleteFiles(files, events);
                }
              }}
              onCancel={() => passwordModalInstance.destroy()}
            />
          ),
          footer: null,
        });
        return;
      }

      // If any file selected has shortcuts, show a confirmation modal
      if (files.some((f) => f.hasShortcuts)) {
        const modalInstance = modal.confirm({
          title: t('modal.filesWithShortcutsDeleteConfirmation.title', {
            resource: files?.[0]?.fileName,
            count: files.length,
          }),
          content: (
            <Trans>
              {t('modal.filesWithShortcutsDeleteConfirmation.description', {
                resource: files?.[0]?.fileName,
                count: files.length,
              })}
            </Trans>
          ),
          onOk: () => {
            deleteFiles(files, events);
          },
          onCancel: () => {
            modalInstance.destroy();
          },
        });
      } else {
        // Soft-delete (move to trash) immediately
        deleteFiles(files, events);
      }
    },
    [deleteFiles, modal, notification, t, deps.authenticatedUser?.id],
  );

  /**
   * Handle restoring files from trash, using provided callback.
   */
  const handleRestoreFilesFromTrash = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      restoreFilesFromTrash(files, events);
    },
    [restoreFilesFromTrash],
  );

  /**
   * Show confirmation modal and permanently delete files from trash on confirmation.
   */
  const handlePermanentlyDeleteFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      // Show confirmation modal
      const modalInstance = modal.confirm({
        title: t('modal.filePermanentlyDeleteForm.title', {
          resource: files?.[0]?.fileName,
          count: files.length,
        }),
        content: (
          <Trans>
            {t('modal.filePermanentlyDeleteForm.description', {
              resource: files?.[0]?.fileName,
              count: files.length,
            })}
          </Trans>
        ),
        okText: t('common.delete'),
        cancelText: t('common.cancel'),
        okType: 'danger',
        onOk: () => {
          // Actually perform permanent deletion after confirmation
          permanentlyDeleteFiles(files, events);
        },
        onCancel: () => {
          modalInstance.destroy();
        },
      });
    },
    [permanentlyDeleteFiles, modal, t],
  );

  /**
   * Show modal to confirm emptying the entire trash bin.
   */
  const handleEmptyTrash = useCallback(
    (events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.emptyFilesTrash.title'),
        content: (
          <Trans>
            {t('modal.emptyFilesTrash.description', {
              keyword: t('common.delete'),
            })}
          </Trans>
        ),
        okText: t('common.delete'),
        onOk: () => {
          emptyTrash(events);
        },
        onCancel: () => {
          modalInstance.destroy();
        },
      });
    },
    [modal, t, emptyTrash],
  );

  /**
   * Show modal to offer restoring/confirmation when user tries to open a file from trash.
   */
  const handleOpenFileFromTrash = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.openFileFromTrash.title', {
          resource: file?.fileName,
        }),
        content: (
          <Trans>
            {t('modal.openFileFromTrash.description', {
              resource: file?.fileName,
            })}
          </Trans>
        ),
        okText: t('common.restore'),
        onOk: () => {
          restoreFilesFromTrash([file], events);
        },
        onCancel: () => {
          modalInstance.destroy();
        },
      });
    },
    [restoreFilesFromTrash, modal, t],
  );

  // Return object: all the main file trash actions as individual functions
  return {
    handleDeleteFiles,
    handleRestoreFilesFromTrash,
    handlePermanentlyDeleteFiles,
    handleEmptyTrash,
    handleOpenFileFromTrash,
  };
}
